/**
 * loadBehaviour — turns a COMPILED script module (TS stripped to CommonJS JS by the
 * editor's esbuild/swc transform) into a {@link BehaviourCtor}.
 *
 * Runtime-only and dependency-free: it evaluates the module with a tiny `require` shim that
 * hands the script the engine modules it imports (e.g.
 * `const { Behaviour, Components } = require('@engine/core')`). The same evaluated JS runs in
 * browser V8, the .NET player's ClearScript V8, AND the editor (play-mode preview) — nothing
 * per-script crosses a language boundary.
 *
 * Expected module shape (what `export default class extends Behaviour {}` compiles to under
 * CommonJS): a `default` export on `module.exports`/`exports`.
 */

import type { BehaviourCtor } from "./TypeScriptExecutor";

interface ModuleExports {
  default?: BehaviourCtor;
  [key: string]: unknown;
}

/**
 * @param jsSource Compiled CommonJS JavaScript (types already stripped).
 * @param modules  Virtual modules the script may `require` (e.g. `{ '@engine/core': {...} }`).
 */
export function loadBehaviour(jsSource: string, modules: Record<string, unknown> = {}): BehaviourCtor {
  const exports: ModuleExports = {};
  const moduleObj = { exports } as { exports: ModuleExports };

  const requireShim = (name: string): unknown => {
    if (Object.prototype.hasOwnProperty.call(modules, name)) return modules[name];
    throw new Error(`[loadBehaviour] module '${name}' is not provided to the script sandbox`);
  };

  // eslint-disable-next-line no-new-func
  const factory = new Function("require", "exports", "module", `"use strict";\n${jsSource}\n`);
  factory(requireShim, exports, moduleObj);

  const exported = moduleObj.exports ?? exports;
  let ctor = exported.default;
  if (typeof ctor !== "function") {
    // Fallback: a named class export (`export class Mover extends Behaviour {}`), not just default.
    for (const key of Object.keys(exported)) {
      if (key !== "default" && typeof exported[key] === "function") {
        ctor = exported[key] as BehaviourCtor;
        break;
      }
    }
  }
  if (typeof ctor !== "function") {
    throw new Error("[loadBehaviour] script has no exported Behaviour class");
  }
  return ctor;
}
